// La liste des devis : paginée, filtrée, avec recherche globale.
// La recherche porte aussi sur la RÉFÉRENCE DE PIÈCE : c'est ainsi
// qu'un deviseur retrouve un devis (« on a déjà fait la 104-05-6 »).

import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Button from '../../ui/Button.jsx';
import { TextField, Select } from '../../ui/TextField.jsx';
import Modal from '../../ui/Modal.jsx';
import ClientPicker from '../../ui/ClientPicker.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';
import { formatEuro, formatDate } from '../../lib/format.js';
import { useSession } from '../../lib/session.jsx';
import ClientCell from './ClientCell.jsx';
import SupprimerDevis from './SupprimerDevis.jsx';
import { useSelectionDevis } from './SelectionDevis.jsx';
import { FILTRES_STATUT } from './statuts.js';
import StatutDevis from './StatutDevis.jsx';


export function DevisListPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), per_page: '25' });
    if (q) params.set('q', q);
    if (status) params.set('status', status);
    return api.get(`/devis?${params}`);
  }, [q, status, page]);

  const { data, error, loading, reload } = useResource(load, [q, status, page]);
  const { isAdmin } = useSession();
  const [duplique, setDuplique] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);
  const selection = useSelectionDevis({ toast, onFait: reload });

  /**
   * DUPLIQUER : la copie reprend tout — pièces, gamme, paliers,
   * sous-traitance — et garde les taux photographiés de l'original.
   * On ouvre la copie : dupliquer sans y aller oblige à la chercher
   * dans une liste où elle ressemble à toutes les autres.
   */
  async function dupliquer(d) {
    setDuplique(d.id);
    try {
      const res = await api.post(`/devis/${d.id}/duplication`);
      toast.success(
        `Devis ${res.devis.reference} créé depuis ${d.reference} — `
        + 'les plans ne sont pas repris, ils restent sur l’original.',
      );
      navigate(`/devis/${res.devis.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDuplique(null);
    }
  }

  const columns = [
    selection.colonne,
    {
      key: 'reference',
      label: 'Référence',
      render: (d) => <Link to={`/devis/${d.id}`}>{d.reference}</Link>,
    },
    { key: 'client_company', label: 'Client', render: (d) => <ClientCell devis={d} /> },
    { key: 'project', label: 'Projet', render: (d) => d.project || '—' },
    { key: 'piece_count', label: 'Pièces', numeric: true },
    { key: 'total_ht', label: 'Total HT', numeric: true, render: (d) => formatEuro(d.total_ht) },
    {
      key: 'status',
      label: 'Statut',
      // Modifiable depuis la liste (6 octobre 2026) — `StatutDevis.jsx`.
      render: (d) => <StatutDevis devis={d} toast={toast} onChange={reload} />,
    },
    { key: 'created_at', label: 'Créé le', render: (d) => formatDate(d.created_at) },
    {
      // DEUX GESTES, DEUX ICÔNES, là où l'on fait le ménage. Supprimer : un
      // brouillon par tous, le reste par l'admin avec recopie (6 octobre 2026).
      key: 'actions',
      label: '',
      render: (d) => (
        <span className="row row--serre">
          <Button
            size="sm" variant="ghost" icon
            title={`Dupliquer ${d.reference}`}
            aria-label={`Dupliquer le devis ${d.reference}`}
            disabled={duplique === d.id}
            onClick={() => dupliquer(d)}
          >
            ⧉
          </Button>
          {isAdmin || d.status === 'draft' ? (
            <Button
              size="sm" variant="ghost" icon
              title={`Supprimer ${d.reference}`}
              aria-label={`Supprimer le devis ${d.reference}`}
              onClick={() => setASupprimer(d)}
            >
              ✕
            </Button>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <>
      <Card>
        <CardHead title="Devis">
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            Nouveau devis
          </Button>
        </CardHead>
        <CardBody className="stack">
          <div className="row">
            <TextField
              label="Rechercher"
              placeholder="Référence, client, projet, référence de pièce…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
            <Select
              label="Statut"
              value={status}
              options={FILTRES_STATUT}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            />
            {selection.barre}
          </div>

          {loading ? (
            <SkeletonRows rows={5} />
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : (
            <>
              <Table
                columns={columns}
                rows={data.rows}
                caption="Liste des devis"
                empty={
                  <EmptyState title={q || status ? 'Aucun devis ne correspond' : 'Aucun devis'}>
                    {q || status
                      ? 'Modifiez la recherche ou le filtre.'
                      : 'Créez le premier devis pour commencer à chiffrer.'}
                  </EmptyState>
                }
              />
              {data.total > data.per_page ? (
                <div className="row row--between">
                  <span className="field__hint">
                    {data.total} devis — page {data.page} sur {Math.ceil(data.total / data.per_page)}
                  </span>
                  <div className="row">
                    <Button size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                      Précédent
                    </Button>
                    <Button
                      size="sm"
                      disabled={page >= Math.ceil(data.total / data.per_page)}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>

      <NouveauDevis
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(devis) => {
          toast.success(`Devis ${devis.reference} créé.`);
          navigate(`/devis/${devis.id}`);
        }}
        onError={(err) => toast.error(err.message)}
      />

      {/* UNE seule boîte de confirmation, montée hors du tableau et
          pilotée par la ligne. Ce qui ne doit pas exister en double, ce
          n'est pas le bouton — c'est la CONFIRMATION : deux boîtes
          finiraient par diverger, et c'est la plus laxiste qui
          servirait. */}
      {aSupprimer ? (
        <SupprimerDevis
          devis={aSupprimer}
          pieces={aSupprimer.piece_count ?? 0}
          sansBouton
          ouvertExterne
          onFermer={() => setASupprimer(null)}
          onSupprime={async () => {
            setASupprimer(null);
            toast.success('Devis supprimé.');
            await reload();
          }}
        />
      ) : null}
    </>
  );
}

function NouveauDevis({ open, onClose, onCreated, onError }) {
  // Le picker cherche lui-même, à chaque frappe, côté SERVEUR. On ne
  // précharge donc plus de liste ici — et surtout plus une liste
  // TRONQUÉE : `per_page=100` rendait invisibles la moitié des 199
  // clients actifs, sans que rien ne le dise.
  //
  // Il reste un appel, uniquement pour répondre à « y a-t-il seulement
  // un client ? ». Sans lui, une base vide afficherait une zone de
  // recherche qui ne trouve jamais rien, au lieu de dire quoi faire.
  const loadClients = useCallback(() => api.get('/clients?per_page=1&active=true'), []);
  const { data, error, loading } = useResource(loadClients, [open]);
  const [client, setClient] = useState(null);
  const [project, setProject] = useState('');
  const [pending, setPending] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setPending(true);
    try {
      const res = await api.post('/devis', {
        client_id: client.id,
        project: project || null,
      });
      onCreated(res.devis);
    } catch (err) {
      onError(err);
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nouveau devis"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" form="form-nouveau-devis" disabled={pending || !client}>
            {pending ? 'Création…' : 'Créer'}
          </Button>
        </>
      }
    >
      {loading ? (
        <SkeletonRows rows={2} />
      ) : error ? (
        <ErrorState error={error} />
      ) : data.total === 0 ? (
        <EmptyState title="Aucun client">
          Un devis part d’un client. Créez-en un d’abord dans l’écran Clients.
        </EmptyState>
      ) : (
        <form id="form-nouveau-devis" className="stack" onSubmit={submit}>
          <ClientPicker
            label="Client"
            required
            selected={client}
            hint="Tapez les premières lettres du nom, de la ville ou du numéro client."
            onSelect={setClient}
          />
          <TextField
            label="Projet"
            value={project}
            hint="La référence est attribuée par le programme, pas saisie."
            onChange={(e) => setProject(e.target.value)}
          />
        </form>
      )}
    </Modal>
  );
}

export default DevisListPage;
